import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Typography,
  Chip,
  Grid,
  MenuItem,
  IconButton,
  Switch,
} from '@mui/material';
import {
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { API } from '../services/api';
import { showSuccess, showError } from '../utils/notifications';
import DataTable from '../components/DataTable';
import FilterBar from '../components/FilterBar';
import ConfirmDialog from '../components/ConfirmDialog';
import ActionButton from '../components/ActionButton';
import TableSkeleton from '../components/TableSkeleton';

const MotionBox = motion(Box);

function ScheduledJobs() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, jobId: null });
  const [editingJob, setEditingJob] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [scheduleTypeFilter, setScheduleTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [externalApis, setExternalApis] = useState([]);
  const [formData, setFormData] = useState({
    job_name: '',
    job_description: '',
    job_category: '',
    is_enabled: true,
    priority: 5,
    schedule_type: 'interval',
    cron_expression: '',
    interval_seconds: 3600,
    run_at: '',
    action_type: 'http',
    action_target: '',
    action_payload: '{}',
    external_api_id: null,
    max_retries: 3,
    concurrency_policy: 'skip',
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [jobsResponse, apisResponse] = await Promise.all([
        API.scheduler.jobs.list(),
        API.externalApis.list()
      ]);
      setJobs(jobsResponse?.data || []);
      setExternalApis(apisResponse?.data || []);
    } catch (err) {
      showError('Failed to load scheduled jobs');
      console.error('Scheduled jobs fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleOpenDialog = (job = null) => {
    if (job) {
      setEditingJob(job);
      setFormData({
        job_name: job.job_name || '',
        job_description: job.job_description || '',
        job_category: job.job_category || '',
        is_enabled: job.is_enabled !== false,
        priority: job.priority || 5,
        schedule_type: job.schedule_type || 'interval',
        cron_expression: job.cron_expression || '',
        interval_seconds: job.interval_seconds || 3600,
        run_at: job.run_at ? new Date(job.run_at).toISOString().slice(0, 16) : '',
        action_type: job.action_type || 'http',
        action_target: job.action_target || '',
        action_payload: job.action_payload ? JSON.stringify(job.action_payload, null, 2) : '{}',
        external_api_id: job.external_api_id || null,
        max_retries: job.max_retries || 3,
        concurrency_policy: job.concurrency_policy || 'skip',
      });
    } else {
      setEditingJob(null);
      setFormData({
        job_name: '',
        job_description: '',
        job_category: '',
        is_enabled: true,
        priority: 5,
        schedule_type: 'interval',
        cron_expression: '',
        interval_seconds: 3600,
        run_at: '',
        action_type: 'http',
        action_target: '',
        action_payload: '{}',
        external_api_id: null,
        max_retries: 3,
        concurrency_policy: 'skip',
      });
    }
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setEditingJob(null);
  };

  const handleSubmit = async () => {
    try {
      const submitData = { ...formData };
      
      // Parse action_payload JSON
      try {
        if (submitData.action_payload && typeof submitData.action_payload === 'string') {
          submitData.action_payload = JSON.parse(submitData.action_payload);
        }
      } catch (e) {
        showError('Invalid JSON in action payload: ' + e.message);
        return;
      }
      
      // Only send relevant schedule fields based on schedule_type
      if (formData.schedule_type !== 'cron') delete submitData.cron_expression;
      if (formData.schedule_type !== 'interval') delete submitData.interval_seconds;
      if (formData.schedule_type !== 'once') delete submitData.run_at;
      
      // If action_target is empty, remove it (it's optional)
      if (!submitData.action_target || submitData.action_target.trim() === '') {
        delete submitData.action_target;
      }
      
      // Remove external_api_id if null or empty
      if (!submitData.external_api_id) {
        delete submitData.external_api_id;
      }

      if (editingJob) {
        await API.scheduler.jobs.update(editingJob.job_id, submitData);
        showSuccess('Job updated successfully!');
      } else {
        // Remove is_enabled when creating (not allowed in CREATE schema)
        const { is_enabled, ...createData } = submitData;
        await API.scheduler.jobs.create(createData);
        showSuccess('Job created successfully!');
      }
      handleCloseDialog();
      fetchData();
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
        (editingJob ? 'Failed to update job' : 'Failed to create job');
      showError(errorMessage);
      console.error('Scheduled job submit error:', err);
    }
  };

  const handleDeleteClick = (jobId) => {
    setConfirmDialog({ open: true, jobId });
  };

  const handleDelete = async () => {
    try {
      await API.scheduler.jobs.delete(confirmDialog.jobId);
      showSuccess('Job deleted successfully!');
      fetchData();
      setConfirmDialog({ open: false, jobId: null });
    } catch (err) {
      showError('Failed to delete job');
      console.error('Scheduled job delete error:', err);
    }
  };

  const handleStatusToggle = async (jobId, currentStatus) => {
    try {
      const newStatus = !currentStatus;
      await API.scheduler.jobs.update(jobId, { is_enabled: newStatus });
      showSuccess(`Job ${newStatus ? 'enabled' : 'disabled'} successfully!`);
      fetchData();
    } catch (err) {
      showError('Failed to update job status');
      console.error('Job status update error:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'error';
      case 'running': return 'info';
      default: return 'default';
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = job.job_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.job_category?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesScheduleType = scheduleTypeFilter === 'all' || job.schedule_type === scheduleTypeFilter;
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'enabled' && job.is_enabled) ||
      (statusFilter === 'disabled' && !job.is_enabled);
    
    return matchesSearch && matchesScheduleType && matchesStatus;
  });

  const uniqueCategories = [...new Set(jobs.map(j => j.job_category).filter(Boolean))];

  const columns = [
    {
      field: 'job_name',
      headerName: 'Job Name',
      width: '16%',
      render: (row) => (
        <Typography variant="body2" fontWeight={600}>
          {row.job_name}
        </Typography>
      ),
    },
    {
      field: 'job_category',
      headerName: 'Category',
      width: '10%',
      render: (row) => row.job_category || '-',
    },
    {
      field: 'external_api',
      headerName: 'External API',
      width: '12%',
      render: (row) => row.external_api ? (
        <Typography variant="body2" fontSize="0.8rem">
          {row.external_api.api_name}
        </Typography>
      ) : '-',
    },
    {
      field: 'schedule_type',
      headerName: 'Schedule',
      width: '10%',
      render: (row) => (
        <Chip 
          label={row.schedule_type?.toUpperCase()} 
          size="small" 
          variant="outlined"
        />
      ),
    },
    {
      field: 'is_enabled',
      headerName: 'Status',
      width: '10%',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch
            checked={row.is_enabled || false}
            onChange={() => handleStatusToggle(row.job_id, row.is_enabled)}
            size="small"
            color="success"
          />
          <Typography variant="caption" color={row.is_enabled ? 'success.main' : 'text.secondary'}>
            {row.is_enabled ? 'Enabled' : 'Disabled'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: '10%',
      render: (row) => row.priority || '-',
    },
    {
      field: 'last_run_at',
      headerName: 'Last Run',
      width: '15%',
      render: (row) => row.last_run_at ? (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {new Date(row.last_run_at).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(row.last_run_at).toLocaleTimeString()}
          </Typography>
        </Box>
      ) : (
        <Chip label="Never" size="small" variant="outlined" />
      ),
    },
    {
      field: 'next_run_at',
      headerName: 'Next Run',
      width: '15%',
      render: (row) => row.next_run_at ? (
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {new Date(row.next_run_at).toLocaleDateString()}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {new Date(row.next_run_at).toLocaleTimeString()}
          </Typography>
        </Box>
      ) : '-',
    },
  ];

  const filterFields = [
    {
      label: 'Schedule Type',
      value: scheduleTypeFilter,
      onChange: setScheduleTypeFilter,
      options: [
        { value: 'all', label: 'All Types' },
        { value: 'cron', label: 'Cron' },
        { value: 'interval', label: 'Interval' },
        { value: 'once', label: 'Once' },
      ],
    },
    {
      label: 'Status',
      value: statusFilter,
      onChange: setStatusFilter,
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'enabled', label: 'Enabled' },
        { value: 'disabled', label: 'Disabled' },
      ],
    },
  ];

  if (loading) {
    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Scheduled Jobs
          </Typography>
        </Box>
        <TableSkeleton columns={8} rows={6} />
      </Box>
    );
  }

  return (
    <MotionBox
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
            Scheduled Jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage automated tasks and schedules
          </Typography>
        </Box>
        <ActionButton
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Job
        </ActionButton>
      </Box>

      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search jobs..."
        filterFields={filterFields}
      />

      <DataTable
        columns={columns}
        data={filteredJobs}
        onEdit={handleOpenDialog}
        onDelete={(row) => handleDeleteClick(row.job_id)}
        getRowId={(row) => row.job_id}
        emptyMessage="No scheduled jobs found"
      />

      <Dialog 
        open={open} 
        onClose={handleCloseDialog} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
        }}>
          <Typography variant="h6" fontWeight={700}>
            {editingJob ? 'Edit Scheduled Job' : 'Add New Scheduled Job'}
          </Typography>
          <IconButton onClick={handleCloseDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Job Name"
                value={formData.job_name}
                onChange={(e) => setFormData({ ...formData, job_name: e.target.value })}
                required
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Category"
                value={formData.job_category}
                onChange={(e) => setFormData({ ...formData, job_category: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Schedule Type"
                value={formData.schedule_type}
                onChange={(e) => setFormData({ ...formData, schedule_type: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="cron">Cron Expression</MenuItem>
                <MenuItem value="interval">Interval</MenuItem>
                <MenuItem value="once">Run Once</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Priority (1-10)"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 10 }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            
            {formData.schedule_type === 'cron' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Cron Expression"
                  value={formData.cron_expression}
                  onChange={(e) => setFormData({ ...formData, cron_expression: e.target.value })}
                  placeholder="0 */6 * * *"
                  helperText="E.g., '0 */6 * * *' runs every 6 hours"
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
            )}

            {formData.schedule_type === 'interval' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Interval (seconds)"
                  type="number"
                  value={formData.interval_seconds}
                  onChange={(e) => setFormData({ ...formData, interval_seconds: parseInt(e.target.value) })}
                  helperText="Time between executions in seconds"
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
            )}

            {formData.schedule_type === 'once' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Run At"
                  type="datetime-local"
                  value={formData.run_at}
                  onChange={(e) => setFormData({ ...formData, run_at: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Action Type"
                value={formData.action_type}
                onChange={(e) => setFormData({ ...formData, action_type: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="http">HTTP Request</MenuItem>
                <MenuItem value="function">Function</MenuItem>
                <MenuItem value="script">Script</MenuItem>
              </TextField>
            </Grid>
            
            {formData.action_type === 'http' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="External API"
                  value={formData.external_api_id || ''}
                  onChange={(e) => setFormData({ ...formData, external_api_id: e.target.value || null })}
                  helperText="Select an external API to call"
                  sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {externalApis.map((api) => (
                    <MenuItem key={api.api_id} value={api.api_id}>
                      {api.api_name} ({api.method} - {api.base_url})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            
            <Grid item xs={12} sm={formData.action_type === 'http' ? 12 : 6}>
              <TextField
                fullWidth
                label="Action Target"
                value={formData.action_target}
                onChange={(e) => setFormData({ ...formData, action_target: e.target.value })}
                placeholder="URL, function name, or script path"
                helperText={formData.action_type === 'http' ? "Optional: Override or extend the External API URL" : ""}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Action Payload"
                value={formData.action_payload}
                onChange={(e) => setFormData({ ...formData, action_payload: e.target.value })}
                multiline
                rows={4}
                required
                placeholder='{"key": "value", "param": "data"}'
                helperText="JSON object containing the payload for the action (required)"
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Retries"
                type="number"
                value={formData.max_retries}
                onChange={(e) => setFormData({ ...formData, max_retries: parseInt(e.target.value) })}
                inputProps={{ min: 0 }}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Concurrency Policy"
                value={formData.concurrency_policy}
                onChange={(e) => setFormData({ ...formData, concurrency_policy: e.target.value })}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              >
                <MenuItem value="skip">Skip</MenuItem>
                <MenuItem value="queue">Queue</MenuItem>
                <MenuItem value="replace">Replace</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.job_description}
                onChange={(e) => setFormData({ ...formData, job_description: e.target.value })}
                multiline
                rows={3}
                sx={{ '& .MuiInputLabel-root': { backgroundColor: 'white', px: 0.5 } }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <Box sx={{ px: 3, pb: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <ActionButton variant="outlined" onClick={handleCloseDialog}>
            Cancel
          </ActionButton>
          <ActionButton variant="contained" onClick={handleSubmit}>
            {editingJob ? 'Update Job' : 'Create Job'}
          </ActionButton>
        </Box>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, jobId: null })}
        onConfirm={handleDelete}
        title="Delete Scheduled Job"
        message="Are you sure you want to delete this scheduled job? This action cannot be undone."
        confirmText="Delete"
        type="danger"
      />
    </MotionBox>
  );
}

export default ScheduledJobs;
